import React from "react";
import { Check, Users, Armchair, Tv, Wifi, Sparkles, Car, Coffee } from "lucide-react";
import { Reveal } from "../Reveal";
import { useForm } from "../../context/FormContext";
import { PRICING } from "../../config";

const AMENITIES = [
  { icon: Users, big: "Flexible", label: "Capacity by configuration" },
  { icon: Armchair, big: "30", label: "Chairs for classroom setups" },
  { icon: Tv, big: "TV", label: "For presentations" },
  { icon: Wifi, big: "Wi-Fi", label: "Throughout the space" },
  { icon: Sparkles, big: "Mirror", label: "For movement & instruction" },
  { icon: Car, big: "Free", label: "Parking" },
];

export function Booking() {
  const { requestAvailability } = useForm();
  return (
    <section id="pricing" className="bg-cream py-20 md:py-28">
      <div className="max-w-[1320px] mx-auto px-5 md:px-8">
        {/* Amenities */}
        <Reveal><span className="sticker">Amenities & booking</span></Reveal>
        <Reveal delay={0.05}>
          <h2 className="display text-berry text-4xl sm:text-5xl md:text-6xl mt-6 max-w-2xl">Everything you need, included.</h2>
        </Reveal>
        <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-5">
          {AMENITIES.map((a, i) => (
            <Reveal key={a.label} delay={i * 0.05} y={20}>
              <div className="card-soft bg-sky p-6 h-full">
                <a.icon size={22} strokeWidth={2} className="text-berry mb-4" />
                <p className="display text-2xl sm:text-3xl text-charcoal">{a.big}</p>
                <p className="mt-1 text-sm text-charcoal/70">{a.label}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Standard pricing */}
        <Reveal delay={0.05}>
          <h3 className="display text-berry text-3xl md:text-4xl mt-16">Simple, flexible pricing</h3>
        </Reveal>
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-5">
          {PRICING.standard.map((p, i) => (
            <Reveal key={p.label} delay={i * 0.06} y={20}>
              <div className="card-soft bg-cream p-6 h-full">
                <p className="overline text-berry/70">{p.label}</p>
                <p className="display text-4xl md:text-5xl mt-3 text-berry">{p.price}</p>
                {p.sub && <p className="mt-3 text-xs text-charcoal/60">{p.sub}</p>}
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.05}>
          <p className="mt-6 text-sm text-charcoal/70 max-w-2xl">
            Evenings (after 5pm) and weekends: add $15 per hour, with a 2-hour minimum. One-hour
            reservations are available on weekdays before 5pm. Reservations that run past the reserved
            time are charged $30 for every additional 15 minutes. A DMediaClub representative remains
            onsite during every reservation.
          </p>
        </Reveal>

        {/* Brand experiences + offers */}
        <div className="mt-12 grid lg:grid-cols-3 gap-6">
          <Reveal y={24}>
            <div className="card-soft bg-berry text-cream p-8 h-full">
              <p className="overline" style={{ color: "#FBE08A" }}>Curated Brand Experiences</p>
              <p className="display text-5xl mt-4" style={{ color: "#FBE08A" }}>$250</p>
              <p className="mt-2 text-sm text-cream/80">Starting price · two-hour minimum.</p>
              <p className="mt-4 text-sm text-cream/75">Activities with vendors, sponsors, refreshments, ticket sales or production are curated brand experiences. A refundable $75 security & cleaning deposit applies.</p>
              <button onClick={() => requestAvailability({ use: "Curated Brand Experience" })} className="btn mt-6 bg-butter text-charcoal border-2 border-charcoal px-6 py-3 shadow-[3px_3px_0_#2A1020]">Request a Custom Proposal</button>
            </div>
          </Reveal>
          <Reveal delay={0.08} y={24}>
            <div className="card-soft bg-blush p-8 h-full" style={{ background: "#F7C9DC" }}>
              <span className="sticker bg-cream text-xs">Opening offer</span>
              <p className="display text-4xl mt-4 text-berry">{PRICING.openingOffer.price} <span className="text-lg text-charcoal/60">/ 3 Hours</span></p>
              <p className="mt-1 text-sm text-charcoal/60 line-through">Regular: {PRICING.openingOffer.regular}</p>
              <p className="mt-3 text-sm text-charcoal/80">For the first 10 approved bookings. New clients only. Not valid for Curated Brand Experiences.</p>
              <button onClick={() => requestAvailability({ use: "Workshop or Class", duration: "3 hours", tag: "opening_offer" })} className="btn mt-6 bg-berry text-cream border-2 border-berry px-6 py-3 shadow-[3px_3px_0_#2A1020]">Claim the Offer</button>
            </div>
          </Reveal>
          <Reveal delay={0.16} y={24}>
            <div className="card-soft bg-lavender p-8 h-full" style={{ background: "#DCCDF2" }}>
              <span className="sticker bg-cream text-xs">Founding host package</span>
              <p className="display text-4xl mt-4 text-berry">{PRICING.founding.price}</p>
              <p className="mt-1 text-sm text-charcoal/60 line-through">Regular value: {PRICING.founding.regular}</p>
              <p className="mt-3 text-sm text-charcoal/80">Four three-hour reservations · 12 total hours · consistent dates · used within 60 days.</p>
              <button onClick={() => requestAvailability({ use: "Workshop or Class", duration: "3 hours", tag: "recurring_package" })} className="btn mt-6 bg-berry text-cream border-2 border-berry px-6 py-3 shadow-[3px_3px_0_#2A1020]">Request Recurring Dates</button>
            </div>
          </Reveal>
        </div>

        {/* Notch add-on */}
        <Reveal delay={0.05}>
          <div className="card-soft bg-sky mt-6 p-6 flex flex-col sm:flex-row sm:items-center gap-4">
            <Coffee size={28} strokeWidth={1.8} className="text-berry shrink-0" />
            <p className="text-sm text-charcoal/80 flex-1"><span className="font-serif text-charcoal">Optional add-on · DMediaClub × NØTCH COFFEE.</span> Request personalized iced lattes for your guests — we’ll follow up with options and pricing.</p>
            <button onClick={() => requestAvailability({ notch: true })} className="btn bg-butter text-charcoal border-2 border-charcoal px-5 py-2.5 text-sm shadow-[3px_3px_0_#2A1020] shrink-0">Ask about lattes</button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
